import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.js';
import { SidebarMobileProvider } from './context/SidebarMobileContext.js';
import { ModalProvider } from './context/ModalContext.js';
import { ScanQrModalProvider } from './context/ScanQrModalContext.js';
import PrivateRoute from './components/common/PrivateRoute.js';
import { hasAttendanceAccess, hasExternalCommissionAccess, hasMlAgentCommissionAccess, hasOgclTransferAccess, hasOgclTransferRequestMenuAccess, canShowOgclTransferCreate, isContractPartyAdmin, hasShipmentCrossingAccess, hasMyPartnershipsAccess, canCreateMlSheet, canCreateMlSheetByQr, canAddMlSheetShipments, canAccessDailyPayments, canAccessMlSheetList, mlSheetUnavailableMessage } from './utils/networkMode.js';

// Auth Pages
import Login from './components/auth/Login.js';
import ForgotPassword from './components/auth/ForgotPassword.js';
import ResetPassword from './components/auth/ResetPassword.js';

// Home Pages
import LandingPage from './components/home/LandingPage.js';
import PublicShipmentTracking from './components/home/PublicShipmentTracking.js';
import PublicTrackByIdRoute from './components/home/PublicTrackByIdRoute.js';
import UserShipmentTracking from './components/home/UserShipmentTracking.js';
import PublicViewReturn from './components/home/PublicViewReturn.js';
import PublicViewShipment from './components/home/PublicViewShipment.js';
import ContactUs from './components/home/ContactUs.js';
import Features from './components/home/Features.js';
import About from './components/home/About.js';
import Services from './components/home/Services.js';
import Navbar from "./components/home/Navbar";

// Dashboard Pages
import MasterAdminDashboard from './components/dashboard/MasterAdminDashboard.js';
import AdminDashboard from './components/dashboard/AdminDashboard.js';
import OwnerDashboard from './components/dashboard/OwnerDashboard.js';
import LocationTargetsPage from './components/dashboard/LocationTargetsPage.js';
import ContractPartyAdminDashboard from './components/dashboard/ContractPartyAdminDashboard.js';
import AccountantDashboard from './components/dashboard/AccountantDashboard.js';
import AgentDashboard from './components/dashboard/AgentDashboard.js';
import StaffDashboard from './components/dashboard/StaffDashboard.js';

// Company Pages
import CompanyList from './components/company/CompanyList.js';
import AddCompany from './components/company/AddCompany.js';
import EditCompany from './components/company/EditCompany.js';
import CompanySettings from './components/company/CompanySettings.js';
import ViewCompany from './components/company/ViewCompany.js';

// Company Subscription Pages
import CompanySubscriptionList from './components/company-subscription/CompanySubscriptionList.js';
import AddCompanySubscription from './components/company-subscription/AddCompanySubscription.js';
import EditCompanySubscription from './components/company-subscription/EditCompanySubscription.js';
import ViewCompanySubscription from './components/company-subscription/ViewCompanySubscription.js';

// WhatsApp Counter Pages
import WhatsAppCounterList from './components/whatsapp-counter/WhatsAppCounterList.js';
import ViewWhatsAppCounter from './components/whatsapp-counter/ViewWhatsAppCounter.js';
import GstVerificationCounterList from './components/gst-verification-counter/GstVerificationCounterList.js';
import ViewGstVerificationCounter from './components/gst-verification-counter/ViewGstVerificationCounter.js';
import EwaybillCounterList from './components/ewaybill-counter/EwaybillCounterList.js';
import ViewEwaybillCounter from './components/ewaybill-counter/ViewEwaybillCounter.js';

// User Pages
import UserList from './components/user/UserList.js';
import AddUser from './components/user/AddUser.js';
import EditUser from './components/user/EditUser.js';
import ViewUser from './components/user/ViewUser.js';

// Role Pages
import RoleList from './components/role/RoleList.js';
import AddRole from './components/role/AddRole.js';
import EditRole from './components/role/EditRole.js';
import ViewRole from './components/role/ViewRole.js';

// Vehicle Pages
import VehicleList from './components/vehicle/VehicleList.js';
import AddVehicle from './components/vehicle/AddVehicle.js';
import EditVehicle from './components/vehicle/EditVehicle.js';
import ViewVehicle from './components/vehicle/ViewVehicle.js';

// Contact Pages
import ContactList from './components/contact/ContactList.js';
import EditContact from './components/contact/EditContact.js';
import ViewContact from './components/contact/ViewContact.js';

// Testimonial Pages
import TestimonialList from './components/testimonial/TestimonialList.js';
import AddTestimonial from './components/testimonial/AddTestimonial.js';
import EditTestimonial from './components/testimonial/EditTestimonial.js';
import ViewTestimonial from './components/testimonial/ViewTestimonial.js';

// Payment Pages
import PaymentList from './components/shipment-payment/PaymentList.js';
import AddPayment from './components/shipment-payment/AddPayment.js';
import EditPayment from './components/shipment-payment/EditPayment.js';
import ViewPayment from './components/shipment-payment/ViewPayment.js';
import DailyEarnings from './components/shipment-payment/DailyEarnings.js';

// Shipment Client Pages
import ShipmentClientList from './components/shipment-client/ShipmentClientList.js';
import AddShipmentClient from './components/shipment-client/AddShipmentClient.js';
import EditShipmentClient from './components/shipment-client/EditShipmentClient.js';
import ViewShipmentClient from './components/shipment-client/ViewShipmentClient.js';
import VendorBatchShipmentBill from './components/shipment-client/VendorBatchShipmentBill.js';

// Shipment Pages
import ShipmentList from './components/shipment/ShipmentList.js';
import AddShipment from './components/shipment/AddShipmentRoute.js';
import EditShipment from './components/shipment/EditShipment.js';
import ViewShipment from './components/shipment/ViewShipment.js';

// Shipment Return Pages
import ReturnList from './components/shipment-return/ReturnList.js';
import AddReturn from './components/shipment-return/AddReturn.js';
import EditReturn from './components/shipment-return/EditReturn.js';
import ViewReturn from './components/shipment-return/ViewReturn.js';

// Location Hub Pages
import LocationHubList from "./components/location-hub/LocationHubList.js"
import AddLocationHub from "./components/location-hub/AddLocationHub.js"
import EditLocationHub from "./components/location-hub/EditLocationHub.js"
import ViewLocationHub from "./components/location-hub/ViewLocationHub.js"

// Shipment Missing Pages
import MissingList from "./components/shipment-missing/MissingList.js"
import AddMissing from "./components/shipment-missing/AddMissing.js"
import EditMissing from "./components/shipment-missing/EditMissing.js"
import ViewMissing from "./components/shipment-missing/ViewMissing.js"

// ML Sheet Pages
import MLSheetList from "./components/ml-sheet/MLSheetList.js"
import AddMLSheet from "./components/ml-sheet/AddMLSheet.js"
import AddMlSheetShipments from "./components/ml-sheet/AddMlSheetShipments.js"
import CreateMLSheetQR from "./components/ml-sheet/CreateMLSheetQR.js"
import EditMLSheet from "./components/ml-sheet/EditMLSheet.js"
import ViewMLSheet from "./components/ml-sheet/ViewMLSheet.js"
import AgentCompanySettlementPage from "./components/company-settlement/AgentCompanySettlementPage.js"
import AccountantCompanySettlementApprovals from "./components/company-settlement/AccountantCompanySettlementApprovals.js"
import { CommissionPaymentsEntry } from "./components/ml-settlement/AgentCommissionPaymentsPage.js"
import CommissionBreakdownPage from "./components/dashboard/CommissionBreakdownPage.js"
import StatusUpdate from "./components/ml-sheet/StatusUpdate.js"
import AgentUpdateStatus from "./components/ml-sheet/AgentUpdateStatus.js"
import OgclTransferRequestList from './components/ogcl-transfer/OgclTransferRequestList.js';
import CreateOgclTransferRequest from './components/ogcl-transfer/CreateOgclTransferRequest.js';
import ViewOgclTransferRequest from './components/ogcl-transfer/ViewOgclTransferRequest.js';

// Agent Network Pages
import PartnerAgentList from "./components/agent-network/PartnerAgentList.js"
import FindAgent from "./components/agent-network/FindAgent.js"
import PartnershipRequests from "./components/agent-network/PartnershipRequests.js"
import MyPartnerships from "./components/agent-network/MyPartnerships.js"

// External Entity Pages
import ExternalMLList from "./components/external-entity/ExternalMLList.js"
import ViewExternalML from "./components/external-entity/ViewExternalML.js"
import ViewExternalShipment from "./components/external-entity/ViewExternalShipment.js"

// On Contract Pages
import OnContractList from "./components/on-contracts/OnContractList"
import AddOnContract from "./components/on-contracts/AddOnContract"
import EditOnContract from "./components/on-contracts/EditOnContract"
import ViewOnContract from "./components/on-contracts/ViewOnContract"

// Salary Pages
import SalaryList from './components/salary/SalaryList.js';
import AddSalary from './components/salary/AddSalary.js';
import EditSalary from './components/salary/EditSalary.js';
import ViewSalary from './components/salary/ViewSalary.js';

// Loan Given Routes
import LoanGivenList from './components/loan-given/LoanGivenList.js';
import AddLoanGiven from './components/loan-given/AddLoanGiven.js';
import EditLoanGiven from './components/loan-given/EditLoanGiven.js';
import ViewLoanGiven from './components/loan-given/ViewLoanGiven.js';
import AddRepaymentGiven from './components/loan-given/AddRepaymentGiven.js';

// Loan Received Routes
import LoanReceivedList from './components/loan-received/LoanReceivedList.js';
import AddLoanReceived from './components/loan-received/AddLoanReceived.js';
import EditLoanReceived from './components/loan-received/EditLoanReceived.js';
import ViewLoanReceived from './components/loan-received/ViewLoanReceived.js';
import AddRepaymentReceived from './components/loan-received/AddRepaymentReceived.js';

// Analytics Routes
import Analytics from './components/analytics/Analytics.js';
import ShipmentAnalytics from './components/analytics/ShipmentAnalytics.js';
import UserPerformanceAnalytics from './components/analytics/UserPerformanceAnalytics.js';
import VehicleAnalytics from './components/analytics/VehicleAnalytics.js';
import MissingReturnedAnalytics from './components/analytics/MissingReturnedAnalytics.js';
import ProfitLossAnalytics from './components/analytics/ProfitLossAnalytics.js';
import OnContractAnalytics from './components/analytics/OnContractAnalytics.js';
import VehicleContractAnalytics from './components/analytics/VehicleContractAnalytics.js';
import TripContractAnalytics from './components/analytics/TripContractAnalytics.js';
import AgentNetworkAnalytics from './components/analytics/AgentNetworkAnalytics.js';
import CommissionAnalytics from './components/analytics/CommissionAnalytics.js';

// Report Routes
import Reports from './components/report/Reports.js';
import ReportDetailPage from './components/report/ReportDetailPage.js';

// Attendance Pages
import AttendanceList from './components/attendance/AttendanceList.js';
import BulkAttendanceLogin from './components/attendance/BulkAttendanceLogin.js';
import ViewAttendance from './components/attendance/ViewAttendance.js';
import ViewAttendanceReport from './components/attendance/ViewAttendanceReport.js';
import EditAttendance from './components/attendance/EditAttendance.js';

// Cashbook Pages
import CashbookReport from './components/cashbook/CashbookReport.js';

// Expense Pages
import ExpenseList from './components/expense/ExpenseList.js';
import AddExpense from './components/expense/AddExpense.js';
import EditExpense from './components/expense/EditExpense.js';
import ViewExpense from './components/expense/ViewExpense.js';
import PaymentIndentQueue from './components/expense/PaymentIndentQueue.js';
import RecurringWeeklyBills from './components/expense/RecurringWeeklyBills.js';
import PlDeskPage from './components/pl-desk/PlDeskPage.js';
import PlStatutoryPage from './components/pl-desk/PlStatutoryPage.js';

// Inventory Pages
import InventoryList from './components/inventory/InventoryList.js';
import AddInventory from './components/inventory/AddInventory.js';
import EditInventory from './components/inventory/EditInventory.js';
import ViewInventory from './components/inventory/ViewInventory.js';

// Inventory Usage Pages
import AddInventoryUsage from './components/inventory-usage/AddInventoryUsage.js';
import EditInventoryUsage from './components/inventory-usage/EditInventoryUsage.js';
import ViewInventoryUsage from './components/inventory-usage/ViewInventoryUsage.js';

// Inventory SKU Pages
import AddInventorySKU from './components/inventory-sku/AddInventorySKU.js';
import EditInventorySKU from './components/inventory-sku/EditInventorySKU.js';
import ViewInventorySKU from './components/inventory-sku/ViewInventorySKU.js';

// External Shipment Commission Pages (EXTERNAL companies)
import ExternalCommissionList from './components/external-shipment-commission/ExternalCommissionList.js';
import AddExternalCommission from './components/external-shipment-commission/AddExternalCommission.js';
import EditExternalCommission from './components/external-shipment-commission/EditExternalCommission.js';
import ViewExternalCommission from './components/external-shipment-commission/ViewExternalCommission.js';
import ExternalCommissionDashboard from './components/external-shipment-commission/ExternalCommissionDashboard.js';
import ExternalHubInsights from './components/external-shipment-commission/ExternalHubInsights.js';
import BulkExternalSettlement from './components/external-shipment-commission/BulkExternalSettlement.js';

// Vehicle Owner Pages
import VehicleOwnerList from './components/vehicle-owner/VehicleOwnerList.js';
import AddVehicleOwner from './components/vehicle-owner/AddVehicleOwner.js';
import EditVehicleOwner from './components/vehicle-owner/EditVehicleOwner.js';
import ViewVehicleOwner from './components/vehicle-owner/ViewVehicleOwner.js';

// Route Pages (ML sheet loading order)
import RouteList from './components/route/RouteList.js';
import AddRoute from './components/route/AddRoute.js';
import EditRoute from './components/route/EditRoute.js';
import ViewRoute from './components/route/ViewRoute.js';

// Contract Party Routes
import ContractPartyList from './components/contract-party/ContractPartyList.js';
import AddContractParty from './components/contract-party/AddContractParty.js';
import EditContractParty from './components/contract-party/EditContractParty.js';
import ViewContractParty from './components/contract-party/ViewContractParty.js';

// QR Status Update
import ScanQrUpdateStatus from './components/shipment-qr-status/ScanQrUpdateStatus.js';

// Shipment Crossing
import ShipmentCrossingPage from './components/shipment-crossing/ShipmentCrossingPage.js';

// Vehicle Contract Routes
import VehicleContractList from './components/vehicle-contract/VehicleContractList.js';
import AddVehicleContract from './components/vehicle-contract/AddVehicleContract.js';
import EditVehicleContract from './components/vehicle-contract/EditVehicleContract.js';
import ViewVehicleContract from './components/vehicle-contract/ViewVehicleContract.js';

// Trip Contract Routes
import TripContractList from './components/trip-contract/TripContractList.js';
import AddTripContract from './components/trip-contract/AddTripContract.js';
import EditTripContract from './components/trip-contract/EditTripContract.js';
import ViewTripContract from './components/trip-contract/ViewTripContract.js';

// Landing Support Pages
import PrivacyPolicy from './components/home/landing-support/PrivacyPolicy.js';
import TermsConditions from './components/home/landing-support/TermsConditions.js';
import RefundCancellationPolicy from './components/home/landing-support/RefundCancellationPolicy.js';
import DQLogix from './components/home/landing-support/DQLogix.js';
import DQIMS from './components/home/landing-support/DQIMS.js';
import WebsiteDM from './components/home/landing-support/WebsiteDM.js';
import BusinessLogin from './components/home/landing-support/BusinessLogin.js';
import JoinPlatformModal from './components/home/landing-support/JoinPlatformModal.js';
import { isPublicShipmentTrackPath } from './utils/publicTrackRoute.js';
import AppVersionGate from './components/common/AppVersionGate.js';


// Layout wrapper for public pages with Navbar
const Layout = ({ children }) => {
  const location = useLocation();
  
  const publicRoutes = [
    '/',
    '/track-shipment',
    '/track_shipment',
    '/public-track-shipment',
    '/public_track_shipment',
    '/view-return',
    '/view-shipment',
    '/public-view-shipment',
    '/contact-us',
    '/features',
    '/about',
    '/services',
    '/privacy-policy',
    '/terms-conditions',
    '/refund-cancellation-policy',
    '/dq-logix',
    '/dq-ims',
    '/website-dm',
    '/business-login',
  ];
  
  const authRoutes = ['/login', '/forgot-password', '/reset-password'];
  const isAuthRoute = authRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  const shouldShowNavbar =
    !isAuthRoute &&
    (publicRoutes.some((route) =>
      route === '/' ? location.pathname === '/' : location.pathname.startsWith(route)
    ) || isPublicShipmentTrackPath(location.pathname));
  
  return (
    <>
      {shouldShowNavbar && <Navbar />}
      {children}
      <JoinPlatformModal />
    </>
  );
};

const ExternalCommissionViewRedirect = () => {
  const { externalCommissionId } = useParams();
  return <Navigate to={`/external-commissions/view/${externalCommissionId}`} replace />;
};

function App() {
  return (
    <Router>
      <AppVersionGate>
      <AuthProvider>
        <ModalProvider>
          <ScanQrModalProvider>
          <SidebarMobileProvider>
          <Layout>
            <Routes>
              {/* Public Home Pages */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/track-shipment" element={<PublicShipmentTracking />} />
              <Route path="/track_shipment" element={<PublicShipmentTracking />} />
              {/* Static WhatsApp / QR deep links land here without a consignment */}
              <Route path="/public-track-shipment" element={<PublicShipmentTracking />} />
              <Route path="/public_track_shipment" element={<PublicShipmentTracking />} />
              {/* Same links with a consignment appended (e.g. /public-track-shipment/83822470) */}
              <Route path="/public-track-shipment/:shipmentId" element={<PublicShipmentTracking />} />
              <Route path="/public_track_shipment/:shipmentId" element={<PublicShipmentTracking />} />
              <Route path="/track-shipment/:shipmentId" element={<PublicShipmentTracking />} />
              <Route path="/track_shipment/:shipmentId" element={<PublicShipmentTracking />} />
              <Route path="/track" element={<Navigate to="/track-shipment" replace />} />
              <Route path="/track/:shipmentId" element={<PublicShipmentTracking />} />
              <Route path="/user/track-shipment" element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver', 'Hub Admin']} element={<UserShipmentTracking />} />} />
              <Route path="/view-return/:returnNo" element={<PublicViewReturn />} />
              <Route path="/view-shipment/:shipmentId" element={<PublicViewShipment />} />
              <Route path="/public-view-shipment/:shipmentId" element={<PublicViewShipment />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />

              {/* Auth Routes - No navbar */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Dashboard Routes */}
              <Route
                path="/system-admin-dashboard"
                element={<PrivateRoute roles={['System Admin']} element={<MasterAdminDashboard />} />}
              />
              <Route
                path="/admin-dashboard"
                element={<PrivateRoute roles={['Admin', 'Hub Admin']} element={<AdminDashboard />} />}
              />
              <Route
                path="/contract-party-dashboard"
                element={<PrivateRoute roles={['Contract Party Admin']} element={<ContractPartyAdminDashboard />} />}
              />
              <Route
                path="/accountant-dashboard"
                element={<PrivateRoute roles={['Accountant']} element={<AccountantDashboard />} />}
              />
              <Route
                path="/agent-dashboard"
                element={<PrivateRoute roles={['Agent']} element={<AgentDashboard />} />}
              />
              <Route
                path="/owner-dashboard"
                element={<PrivateRoute roles={['Owner']} element={<OwnerDashboard />} />}
              />
              <Route
                path="/staff-dashboard"
                element={<PrivateRoute roles={['Staff']} element={<StaffDashboard />} />}
              />
              <Route
                path="/location-targets"
                element={<PrivateRoute roles={['Owner', 'Admin', 'Accountant', 'System Admin', 'Hub Admin', 'Agent']} element={<LocationTargetsPage />} />}
              />

              {/* Company Management Routes */}
              <Route
                path="/companies"
                element={<PrivateRoute roles={['System Admin']} element={<CompanyList />} />}
              />
              <Route
                path="/companies/add"
                element={<PrivateRoute roles={['System Admin']} element={<AddCompany />} />}
              />
              <Route
                path="/companies/edit/:companyId"
                element={<PrivateRoute roles={['System Admin']} element={<EditCompany />} />}
              />
              <Route
                path="/companies/view/:companyId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewCompany />} />}
              />
              <Route
                path="/company-settings"
                element={<PrivateRoute roles={['Admin', 'System Admin', 'Owner']} element={<CompanySettings />} />}
              />

              {/* Company Subscription Routes */}
              <Route
                path="/company-subscriptions"
                element={<PrivateRoute roles={['System Admin']} element={<CompanySubscriptionList />} />}
              />
              <Route
                path="/company-subscriptions/add"
                element={<PrivateRoute roles={['System Admin']} element={<AddCompanySubscription />} />}
              />
              <Route
                path="/company-subscriptions/edit/:subscriptionId"
                element={<PrivateRoute roles={['System Admin']} element={<EditCompanySubscription />} />}
              />
              <Route
                path="/company-subscriptions/view/:subscriptionId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewCompanySubscription />} />}
              />

              {/* WhatsApp Counter Routes */}
              <Route
                path="/company-whatsapp-counters"
                element={<PrivateRoute roles={['System Admin']} element={<WhatsAppCounterList />} />}
              />
              <Route
                path="/company-whatsapp-counters/view/:counterId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewWhatsAppCounter />} />}
              />

              {/* GST Verification Counter Routes */}
              <Route
                path="/company-gst-verification-counters"
                element={<PrivateRoute roles={['System Admin']} element={<GstVerificationCounterList />} />}
              />
              <Route
                path="/company-gst-verification-counters/view/:counterId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewGstVerificationCounter />} />}
              />
              <Route
                path="/company-ewaybill-counters"
                element={<PrivateRoute roles={['System Admin']} element={<EwaybillCounterList />} />}
              />
              <Route
                path="/company-ewaybill-counters/view/:counterId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewEwaybillCounter />} />}
              />

              {/* User Management Routes */}
              <Route
                path="/users"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin', 'Owner']} element={<UserList />} />}
              />
              <Route
                path="/users/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Contract Party Admin', 'Hub Admin']} element={<AddUser />} />}
              />
              <Route
                path="/users/edit/:userId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Contract Party Admin', 'Hub Admin']} element={<EditUser />} />}
              />
              <Route
                path="/users/view/:userId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin', 'Owner']} element={<ViewUser />} />}
              />

              {/* Role Management Routes */}
              <Route
                path="/roles"
                element={<PrivateRoute roles={['System Admin']} element={<RoleList />} />}
              />
              <Route
                path="/roles/add"
                element={<PrivateRoute roles={['System Admin']} element={<AddRole />} />}
              />
              <Route
                path="/roles/edit/:roleId"
                element={<PrivateRoute roles={['System Admin']} element={<EditRole />} />}
              />
              <Route
                path="/roles/view/:roleId"
                element={<PrivateRoute roles={['System Admin']} element={<ViewRole />} />}
              />

              {/* Vehicle Management Routes */}
              <Route
                path="/vehicles"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<VehicleList />} />}
              />
              <Route
                path="/vehicles/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<AddVehicle />} />}
              />
              <Route
                path="/vehicles/edit/:vehicleId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<EditVehicle />} />}
              />
              <Route
                path="/vehicles/view/:vehicleId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<ViewVehicle />} />}
              />

              {/* Contact Management Routes */}
              <Route
                path="/contact"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ContactList />} />}
              />
              <Route
                path="/contact/edit/:contactId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditContact />} />}
              />
              <Route
                path="/contact/view/:contactId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewContact />} />}
              />

              {/* Testimonial Management Routes */}
              <Route
                path="/testimonials"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<TestimonialList />} />}
              />
              <Route
                path="/testimonials/add"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddTestimonial />} />}
              />
              <Route
                path="/testimonials/edit/:testimonialId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditTestimonial />} />}
              />
              <Route
                path="/testimonials/view/:testimonialId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewTestimonial />} />}
              />

              {/* Payment Management Routes */}
              <Route
                path="/payments"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<PaymentList />} />}
              />
              <Route
                path="/payments/add/:shipmentId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<AddPayment />} />}
              />
              <Route
                path="/payments/edit/:paymentId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<EditPayment />} />}
              />
              <Route
                path="/payments/view/:paymentId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ViewPayment />} />}
              />
              <Route
                path="/daily-earnings"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<DailyEarnings />} />}
              />

              {/* Shipment Client Management Routes */}
              <Route
                path="/shipment-clients"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin', 'Agent']} element={<ShipmentClientList />} />}
              />
              <Route
                path="/shipment-clients/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent']} element={<AddShipmentClient />} />}
              />
              <Route
                path="/shipment-clients/edit/:clientId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent']} element={<EditShipmentClient />} />}
              />
              <Route
                path="/shipment-clients/view/:clientId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin', 'Agent']} element={<ViewShipmentClient />} />}
              />
              <Route
                path="/shipment-clients/:clientId/batch-bill"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<VendorBatchShipmentBill />} />}
              />

              {/* Shipment Management Routes */}
              <Route
                path="/shipments"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ShipmentList />} />}
              />
              <Route
                path="/shipments/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent']} element={<AddShipment />} />}
              />
              <Route
                path="/shipments/edit/:shipmentId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent']} element={<EditShipment />} />}
              />
              <Route
                path="/shipments/view/:shipmentId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ViewShipment />} />}
              />
              <Route
                path="/shipment-crossing"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasShipmentCrossingAccess} featureMessage="Shipment crossing is available for internal companies only." element={<ShipmentCrossingPage />} />}
              />
              <Route
                path="/ogcl-transfer-requests"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasOgclTransferRequestMenuAccess} featureMessage="ITCL requests are available for internal companies only." element={<OgclTransferRequestList />} />}
              />
              <Route
                path="/ogcl-transfer-requests/create"
                element={<PrivateRoute roles={['Agent']} allowAccess={canShowOgclTransferCreate} featureMessage="ITCL requests can only be created by ITCL hub agents." element={<CreateOgclTransferRequest />} />}
              />
              <Route
                path="/ogcl-transfer-requests/view/:requestId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasOgclTransferAccess} featureMessage="ITCL requests are available for internal companies only." element={<ViewOgclTransferRequest />} />}
              />

              {/* Agent Network Routes */}
              <Route
                path="/partner-agents"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<PartnerAgentList />} />}
              />
              <Route
                path="/partner-agents/find"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<FindAgent />} />}
              />
              <Route
                path="/partner-agents/requests"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<PartnershipRequests />} />}
              />
              <Route
                path="/my-partnerships"
                element={<PrivateRoute roles={['Agent']} allowAccess={hasMyPartnershipsAccess} featureMessage="My Partnerships is not enabled for your company. Ask your System Admin to enable it under company feature access." element={<MyPartnerships />} />}
              />


              {/* Shipment Return Management Routes */}
              <Route
                path="/returns"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ReturnList />} />}
              />
              <Route
                path="/returns/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent', 'Hub Admin']} element={<AddReturn />} />}
              />
              <Route
                path="/returns/edit/:returnId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent', 'Hub Admin']} element={<EditReturn />} />}
              />
              <Route
                path="/returns/view/:returnId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ViewReturn />} />}
              />

              {/* Location Hub Management Routes */}
              <Route
                path="/location-hubs"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<LocationHubList />} />}
              />
              <Route
                path="/location-hubs/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<AddLocationHub />} />}
              />
              <Route
                path="/location-hubs/edit/:hubId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<EditLocationHub />} />}
              />
              <Route
                path="/location-hubs/view/:hubId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<ViewLocationHub />} />}
              />

              {/* Shipment Missing Management Routes */}
              <Route
                path="/missing-shipments"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<MissingList />} />}
              />
              <Route
                path="/missing-shipments/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent', 'Hub Admin']} element={<AddMissing />} />}
              />
              <Route
                path="/missing-shipments/edit/:missingId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Agent', 'Hub Admin', 'Accountant']} element={<EditMissing />} />}
              />
              <Route
                path="/missing-shipments/view/:missingId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ViewMissing />} />}
              />

              {/* ML Sheet Management Routes */}
              <Route
                path="/ml-sheets"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin']} allowAccess={canAccessMlSheetList} featureMessage={mlSheetUnavailableMessage} element={<MLSheetList />} />}
              />
              <Route
                path="/ml-sheets/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={canCreateMlSheet} featureMessage={mlSheetUnavailableMessage} element={<AddMLSheet />} />}
              />
              <Route
                path="/ml-sheets/create-qr"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={canCreateMlSheetByQr} featureMessage={mlSheetUnavailableMessage} element={<CreateMLSheetQR />} />}
              />
              <Route
                path="/ml-sheets/:mlSheetId/add-shipment"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={canAddMlSheetShipments} featureMessage={mlSheetUnavailableMessage} element={<AddMlSheetShipments />} />}
              />
              <Route
                path="/ml-sheets/edit/:mlSheetId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<EditMLSheet />} />}
              />
              <Route
                path="/ml-sheets/view/:mlSheetId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin']} element={<ViewMLSheet />} />}
              />
              <Route
                path="/ml-sheets/view/:mlSheetId/status-update"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin']} element={<StatusUpdate />} />}
              />
              <Route
                path="/update-status"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Contract Party Admin', 'Hub Admin']} element={<AgentUpdateStatus />} />}
              />
              <Route
                path="/shipment-qr-status"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ScanQrUpdateStatus />} />}
              />
              <Route
                path="/company-settlements"
                element={
                  <PrivateRoute
                    roles={['Agent']}
                    element={<AgentCompanySettlementPage />}
                  />
                }
              />
              <Route
                path="/company-settlement-approvals"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant']}
                    element={<AccountantCompanySettlementApprovals />}
                  />
                }
              />
              <Route
                path="/ml-settlements"
                element={<PrivateRoute roles={['Agent']} element={<Navigate to="/company-settlements" replace />} />}
              />
              <Route
                path="/ml-settlement-approvals"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<Navigate to="/company-settlement-approvals" replace />} />}
              />
              <Route
                path="/ml-commission-payments"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant', 'Contract Party Admin']}
                    element={<CommissionPaymentsEntry />}
                  />
                }
              />
              <Route
                path="/agent-commission-breakdown"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin', 'Owner']}
                    allowAccess={hasMlAgentCommissionAccess}
                    featureMessage="Agent commission breakdown is available for internal companies with Shipment Commission enabled."
                    element={<CommissionBreakdownPage mode="internal" />}
                  />
                }
              />
              <Route
                path="/external-commission-breakdown"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']}
                    allowAccess={hasExternalCommissionAccess}
                    featureMessage="External commission breakdown is available for external network companies."
                    element={<CommissionBreakdownPage mode="external" />}
                  />
                }
              />
              <Route
                path="/paid-amount-settlements"
                element={<PrivateRoute roles={['Agent']} element={<Navigate to="/company-settlements" replace />} />}
              />
              <Route
                path="/paid-amount-settlement-approvals"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<Navigate to="/company-settlement-approvals" replace />} />}
              />

              {/* External Entity Management Routes */}
              <Route
                path="/external-ml"
                element={
                  <PrivateRoute roles={["System Admin", "Admin", "Agent"]} element={<ExternalMLList />} />
                }
              />
              <Route
                path="/external-ml/view/:externalMLId"
                element={
                  <PrivateRoute roles={["System Admin", "Admin", "Agent"]} element={<ViewExternalML />} />
                }
              />
              <Route
                path="/external-ml/:externalMLId/shipment/:shipmentId"
                element={
                  <PrivateRoute roles={["System Admin", "Admin", "Agent"]} element={<ViewExternalShipment />} />
                }
              />

              {/* On Contract Routes */}
              <Route
                path="/on-contracts"
                element={
                  <PrivateRoute roles={["System Admin", "Admin"]} element={<OnContractList />} />
                }
              />
              <Route
                path="/on-contracts/add"
                element={
                  <PrivateRoute roles={["System Admin", "Admin"]} element={<AddOnContract />} />
                }
              />
              <Route
                path="/on-contracts/edit/:contractId"
                element={
                  <PrivateRoute roles={["System Admin", "Admin"]} element={<EditOnContract />} />
                }
              />
              <Route
                path="/on-contracts/view/:contractId"
                element={
                  <PrivateRoute roles={["System Admin", "Admin"]} element={<ViewOnContract />} />
                }
              />

              {/* Salary Management Routes */}
              <Route
                path="/salaries"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<SalaryList />} />}
              />
              <Route
                path="/salaries/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<AddSalary />} />}
              />
              <Route
                path="/salaries/edit/:salaryId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<EditSalary />} />}
              />
              <Route
                path="/salaries/view/:salaryId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<ViewSalary />} />}
              />

              {/* Loan Given Routes */}
              <Route
                path="/loans-given"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<LoanGivenList />} />}
              />
              <Route
                path="/loans-given/add"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddLoanGiven />} />}
              />
              <Route
                path="/loans-given/edit/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditLoanGiven />} />}
              />
              <Route
                path="/loans-given/view/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewLoanGiven />} />}
              />
              <Route
                path="/loans-given/repayment/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddRepaymentGiven />} />}
              />

              {/* Loan Received Routes */}
              <Route
                path="/loans-received"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<LoanReceivedList />} />}
              />
              <Route
                path="/loans-received/add"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddLoanReceived />} />}
              />
              <Route
                path="/loans-received/edit/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditLoanReceived />} />}
              />
              <Route
                path="/loans-received/view/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewLoanReceived />} />}
              />
              <Route
                path="/loans-received/repayment/:loanId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddRepaymentReceived />} />}
              />

              {/* Analytics Routes */}
              <Route
                path="/analytics"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<Analytics />} />}
              />
              <Route
                path="/analytics/shipments"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<ShipmentAnalytics />} />}
              />
              <Route
                path="/analytics/user-performance"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<UserPerformanceAnalytics />} />}
              />
              <Route
                path="/analytics/vehicles"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<VehicleAnalytics />} />}
              />
              <Route
                path="/commission-analytics"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']}
                    element={<CommissionAnalytics />}
                  />
                }
              />
              <Route
                path="/analytics/commissions"
                element={
                  <PrivateRoute
                    roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']}
                    element={<CommissionAnalytics />}
                  />
                }
              />
              <Route
                path="/analytics/missing-returned"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} element={<MissingReturnedAnalytics />} />}
              />
              <Route
                path="/analytics/profit-loss"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<ProfitLossAnalytics />} />}
              />
              <Route
                path="/analytics/agent-network"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<AgentNetworkAnalytics />} />}
              />

              {/* Reports Routes */}
              <Route
                path="/reports"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin', 'Agent', 'Owner']} element={<Reports />} />}
              />
              <Route
                path="/reports/:type"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin', 'Agent', 'Owner']} element={<ReportDetailPage />} />}
              />

              {/* Cashbook Routes */}
              <Route
                path="/cashbook"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<CashbookReport />} />}
              />

              {/* Expense Management Routes */}
              <Route
                path="/pl-desk"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<PlDeskPage />} />}
              />
              <Route
                path="/pl-statutory"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<PlStatutoryPage />} />}
              />
              <Route
                path="/expenses"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Owner', 'Agent']} element={<ExpenseList />} />}
              />
              <Route
                path="/expenses/payment-queue"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={canAccessDailyPayments} featureMessage="Daily Payments is only for the payment executor (RV sir)." element={<PaymentIndentQueue />} />}
              />
              <Route
                path="/expenses/weekly-bills"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Owner']} element={<RecurringWeeklyBills />} />}
              />
              <Route
                path="/expenses/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent']} element={<AddExpense />} />}
              />
              <Route
                path="/expenses/edit/:expenseId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent']} element={<EditExpense />} />}
              />
              <Route
                path="/expenses/view/:expenseId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Owner', 'Agent']} element={<ViewExpense />} />}
              />

              {/* Inventory Management Routes */}
              <Route
                path="/inventory"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<InventoryList />} />}
              />
              <Route
                path="/inventory/add"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddInventory />} />}
              />
              <Route
                path="/inventory/edit/:inventoryId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditInventory />} />}
              />
              <Route
                path="/inventory/view/:inventoryId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewInventory />} />}
              />

              {/* Inventory Usage Management Routes */}
              <Route
                path="/inventory-usage/add/:inventoryId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddInventoryUsage />} />}
              />
              <Route
                path="/inventory-usage/edit/:usageId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditInventoryUsage />} />}
              />
              <Route
                path="/inventory-usage/view/:usageId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewInventoryUsage />} />}
              />

            {/* Inventory SKU Management Routes */}
            <Route
              path="/inventory-sku/add/:inventoryId"
              element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddInventorySKU />} />}
            />
            <Route
              path="/inventory-sku/edit/:skuId"
              element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditInventorySKU />} />}
            />
            <Route
              path="/inventory-sku/view/:skuId"
              element={<PrivateRoute roles={['System Admin', 'Admin']} element={<ViewInventorySKU />} />}
            />

              {/* Attendance Routes */}
              <Route
                path="/attendance"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<AttendanceList />} />}
              />
              <Route
                path="/attendance/bulk-login"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<BulkAttendanceLogin />} />}
              />
              <Route
                path="/attendance/view/:userId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<ViewAttendance />} />}
              />
              <Route
                path="/attendance/report/:userId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<ViewAttendanceReport />} />}
              />
              <Route
                path="/attendance/edit/:attendanceId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} allowAccess={hasAttendanceAccess} featureMessage="Attendance and payroll are not available for external network companies." element={<EditAttendance />} />}
              />

              {/* Internal Partner Payout (shipment_commissions) retired — use /ml-commission-payments */}

              {/* External Shipment Commission — external companies only */}
              <Route
                path="/external-commission-dashboard"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<ExternalCommissionDashboard />} />}
              />
              <Route
                path="/external-commissions"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<ExternalCommissionList />} />}
              />
              <Route
                path="/external-commissions/add"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<AddExternalCommission />} />}
              />
              <Route
                path="/external-commissions/edit/:externalCommissionId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<EditExternalCommission />} />}
              />
              <Route
                path="/external-commissions/bulk-settlement"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<BulkExternalSettlement />} />}
              />
              <Route
                path="/external-commissions/hub-insights"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<ExternalHubInsights />} />}
              />
              <Route
                path="/external-commissions/view/:externalCommissionId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<ViewExternalCommission />} />}
              />
              <Route
                path="/external-commissions/add-payment/:externalCommissionId"
                element={<PrivateRoute roles={['System Admin', 'Admin']} allowAccess={hasExternalCommissionAccess} featureMessage="Commission Record is for external companies only." element={<ExternalCommissionViewRedirect />} />}
              />

              {/* Vehicle Owner Management Routes */}
              <Route
                path="/vehicle-owners"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<VehicleOwnerList />} />}
              />
              <Route
                path="/vehicle-owners/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<AddVehicleOwner />} />}
              />
              <Route
                path="/vehicle-owners/edit/:vehicleOwnerId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<EditVehicleOwner />} />}
              />
              <Route
                path="/vehicle-owners/view/:vehicleOwnerId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<ViewVehicleOwner />} />}
              />

              {/* Route Management (ML sheet loading order) */}
              <Route
                path="/routes"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<RouteList />} />}
              />
              <Route
                path="/routes/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<AddRoute />} />}
              />
              <Route
                path="/routes/edit/:routeId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<EditRoute />} />}
              />
              <Route
                path="/routes/view/:routeId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<ViewRoute />} />}
              />

              {/* Contract Party Management Routes */}
              <Route
                path="/contract-parties"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<ContractPartyList />} />}
              />
              <Route
                path="/contract-parties/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<AddContractParty />} />}
              />
              <Route
                path="/contract-parties/edit/:partyId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<EditContractParty />} />}
              />
              <Route
                path="/contract-parties/view/:partyId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<ViewContractParty />} />}
              />

              {/* Vehicle Contract Management Routes */}
              <Route
                path="/vehicle-contracts"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<VehicleContractList />} />}
              />
              <Route
                path="/vehicle-contracts/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<AddVehicleContract />} />}
              />
              <Route
                path="/vehicle-contracts/edit/:contractId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<EditVehicleContract />} />}
              />
              <Route
                path="/vehicle-contracts/view/:contractId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<ViewVehicleContract />} />}
              />
              <Route
                path="/vehicle-contracts/analytics"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Hub Admin']} element={<VehicleContractAnalytics />} />}
              />

              {/* Trip Contract Management Routes */}
              <Route
                path="/trip-contracts"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<TripContractList />} />}
              />
              <Route
                path="/trip-contracts/add"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<AddTripContract />} />}
              />
              <Route
                path="/trip-contracts/edit/:tripId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<EditTripContract />} />}
              />
              <Route
                path="/trip-contracts/view/:tripId"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant', 'Hub Admin']} element={<ViewTripContract />} />}
              />
              <Route
                path="/trip-contracts/analytics"
                element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<TripContractAnalytics />} />}
              />

              {/* On Contract Analytics */}
              <Route
                path="/on-contracts/analytics"
                element={<PrivateRoute roles={['System Admin', 'Admin']} element={<OnContractAnalytics />} />}
              />

              {/* Landing Support Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />

              {/* Solutions */}
              <Route path="/dq-logix" element={<DQLogix />} />
              <Route path="/dq-ims" element={<DQIMS />} />
              <Route path="/website-dm" element={<WebsiteDM />} />
              <Route path="/business-login" element={<BusinessLogin />} />

              {/* Public shipment deep link: yoursite.com/SO1-ATHN-26-27 */}
              <Route path="/:shipmentId" element={<PublicTrackByIdRoute />} />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
          
            </Routes>
          </Layout>
          </SidebarMobileProvider>
          </ScanQrModalProvider>
        </ModalProvider>
      </AuthProvider>
      </AppVersionGate>
    </Router>
  );
}

export default App;
